import { isPinnedMarketRate } from '../vaults/morpho/rateSanity'
import { isUnearnableRate } from '../vaults/rateSanity'
import type { EarnMarket } from './types'

/**
 * The rate and amount guards, applied to a NORMALIZED earn row.
 *
 * Two guards already existed and neither could reach this surface:
 *
 *  - `vaults/morpho/rateSanity.ts` — the pinned-market runaway — is applied
 *    inside `vaults/morpho/fetchPublic.ts` and `fetchListaFromChain.ts`, i.e.
 *    in the SDK's own Morpho VAULT fetch. `/v1/data/earn` reads a recorder
 *    that never calls it, and the mechanism is not Morpho-specific: **most of
 *    the affected rows are LENDING markets**, which that guard has never
 *    covered at all.
 *  - `vaults/rateSanity.ts` — annualization noise and magnitude-on-dust — is
 *    likewise per-provider.
 *
 * The measured consequence on the served listing (2026-09-08, Ethereum): 98
 * rows over 100 % APR with under 0.1 % of the market withdrawable, holding
 * **the #3, #6 and #8 positions of the whole chain by TVL** —
 * `PAXG-USDC 92` at 800 % on $7.2B with $0 liquidity, `SILO_V2 xUSD/USDC` at
 * 3,254 %. See EARN_COMPLETENESS_AUDIT.md Y2.
 *
 * This module is the ONE place the earn surface asks the question, so both the
 * origin-proxy path and the edge merge get the same answer.
 *
 * ## What it does not do
 *
 * It never clamps a negative rate — `vaults/rateSanity.ts` records at length
 * why, and none of that changes here. It does not re-rate a row either: a
 * matching row is DROPPED, because the pin that inflates the rate is the same
 * pin that stops a depositor getting out, so the row is not a good offer
 * mispriced, it is not an offer.
 */

/**
 * USD TVL above which the number is not a measurement.
 *
 * The largest genuine row we serve is a ~$11B LST; the largest lending market
 * is ~$5B. A hundred billion is two orders of magnitude clear of both and two
 * orders BELOW the observed failures — Euler's `CREDIT` markets on Berachain
 * and Base report $2.18 × 10¹⁷ and $9.06 × 10¹⁶, an unpriceable token valued
 * off a bogus quote. They pass the TVL floor by fourteen orders of magnitude
 * and rank first on any `sort=tvl`.
 */
const IMPOSSIBLE_TVL_USD = 1e11

/**
 * Does this row publish a rate a depositor cannot earn?
 *
 * Mirrors `isUnrealizableRate`'s two-condition discipline exactly, on the
 * normalized fields: an implausible APR **and** a market with essentially
 * nothing withdrawable. Both halves are required and that is the point — a
 * high rate alone can be a genuine incentive campaign, and zero liquidity
 * alone is ordinary for a fully-lent market.
 *
 * `liquidity` ABSENT is not zero. Several providers never report it, and an
 * async-exit vault reports zero by design; condemning either on a field they
 * did not send is the mistake `meetsLiquidityFloor` was written to avoid.
 */
export const isUnrealizableEarnRate = (m: EarnMarket): boolean => {
  const tvl = m.tvl?.formatted
  const liq = m.liquidity?.formatted
  if (tvl === undefined || liq === undefined) return false
  return isPinnedMarketRate({
    aprPercent: m.rate.total,
    totalAssets: tvl,
    liquidity: liq,
  })
}

/**
 * Does this row publish a rate that is an artifact of HOW it was measured —
 * a magnitude no dust vault can support, or a fixed-term annualization
 * blown up a few hours from maturity?
 *
 * Delegates to the cross-provider guard so the thresholds live in one place.
 */
export const isUnearnableEarnRate = (
  m: EarnMarket,
  nowSecs?: number,
): boolean =>
  isUnearnableRate({
    aprPercent: m.rate.total,
    totalAssetsUsd: m.tvl?.usd,
    expiry: m.maturity?.maturity,
    nowSecs,
  })

/**
 * Blank a TVL that cannot be true, in place. Returns whether it did.
 *
 * Blanked rather than dropped, and blanked rather than clamped: the MARKET is
 * usually real, it is the USD valuation that is broken, and `tvl.usd`
 * ABSENT already means "unpriced" everywhere else on this surface — unpriced
 * rows skip the TVL floor and sort last, which is exactly the treatment a
 * number nobody can stand behind deserves. The token amount is left alone; it
 * is not the part that is wrong.
 */
export const repairImpossibleTvl = (m: EarnMarket): boolean => {
  if ((m.tvl?.usd ?? 0) <= IMPOSSIBLE_TVL_USD) return false
  m.tvl.usd = undefined
  if (m.liquidity?.usd !== undefined && m.liquidity.usd > IMPOSSIBLE_TVL_USD) {
    m.liquidity.usd = undefined
  }
  return true
}

export interface EarnSanityResult {
  /** The rows that survived, in their original order. */
  items: EarnMarket[]
  /** Dropped for publishing a rate nobody can earn (the pinned-market case). */
  unrealizable: number
  /** Dropped for publishing a measurement artifact (dust / near-maturity). */
  unearnable: number
  /** Kept, but with a `tvl.usd` that could not be true removed. */
  repricedTvl: number
}

/**
 * Apply every sanity guard to a listing or a page.
 *
 * Mutates `tvl.usd` on repaired rows (the array is freshly normalized on both
 * callers) and returns a new array for the survivors, so a caller that needs
 * the counts for its `excluded` block gets them without a second pass.
 */
export const applyEarnSanity = (
  rows: EarnMarket[],
  opts: { nowSecs?: number } = {},
): EarnSanityResult => {
  const items: EarnMarket[] = []
  let unrealizable = 0
  let unearnable = 0
  let repricedTvl = 0
  for (const m of rows) {
    if (isUnrealizableEarnRate(m)) {
      unrealizable++
      continue
    }
    if (isUnearnableEarnRate(m, opts.nowSecs)) {
      unearnable++
      continue
    }
    if (repairImpossibleTvl(m)) repricedTvl++
    items.push(m)
  }
  return { items, unrealizable, unearnable, repricedTvl }
}
